import { useContext, FC, useState, useEffect } from 'react'

import { Notification, NotificationFilter, Pagination } from 'types'
import readNotifications from 'services/calls/notifications/readNotifications'
import getUnviewedNotificationsCount from 'services/calls/notifications/getUnviewedNotificationsCount'
import { useSocketConnectionManager } from 'contexts/SocketConnection/Provider'
import NotificationsManagerContext from './Context'
import { useAuthentication } from 'contexts/Authentication/Provider'

type SocketParams = {
  count: number
  error?: string
  elements: Notification[]
}

type Props = {
  children: JSX.Element
}

const NotificationsManagerProvider: FC<Props> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [notViewNotificationsCount, setNotViewNotificationsCount] = useState<number>(10)
  const [loadingNotifications, setLoadingNotifications] = useState(true)
  const [loadingMoreNotifications, setLoadingMoreNotifications] = useState(false)
  const [totalNotifications, setTotalNotifications] = useState(0)
  const [filter, setFilter] = useState<NotificationFilter>({ name: '', kind: '', viewed_at: '', opened_at: '' })
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, sort: { by: 'id', order: 'DESC' } })

  const { socket } = useSocketConnectionManager()
  const { user } = useAuthentication()

  function setPaginationPagesIntoLimit() {
    const formatedPagination = { ...pagination }
    if (pagination.page <= 1) return pagination
    const pagesAbovePageOne = formatedPagination.page - 1
    const newLimit = pagesAbovePageOne * formatedPagination.limit
    formatedPagination.page = 1
    formatedPagination.limit = newLimit
    return formatedPagination
  }

  async function loadNotifications() {
    setLoadingNotifications(true)
    const filters = { filter, pagination }
    filters.pagination = setPaginationPagesIntoLimit()
    const response = await readNotifications({ filters })

    const anotherResponse = await getUnviewedNotificationsCount()

    if (anotherResponse?.header?.success) {
      const unviewedNotificationsFromAPI = Number(anotherResponse?.body?.notification_unread)
      setNotViewNotificationsCount(unviewedNotificationsFromAPI)
    }
    if (response?.header?.success) {
      const notificationsFromAPI = response?.body?.notification?.elements
      const totalNotificationsFromAPI = response?.body?.notification?.totalElements
      setNotifications(notificationsFromAPI)
      setTotalNotifications(totalNotificationsFromAPI)
    }
    setLoadingNotifications(false)
  }

  async function loadMoreNotifications() {
    setLoadingMoreNotifications(true)
    const filters = { filter, pagination }
    const response = await readNotifications({ filters })
    if (response?.header?.success) {
      const notificationsFromAPI = response?.body?.notification?.elements
      setNotifications([...notifications, ...notificationsFromAPI])
    }
    setLoadingMoreNotifications(false)
  }

  function setNewNotificationOnSocket(params: SocketParams, currentNotifications: Notification[]) {
    const { elements } = params

    setNotifications([...elements, ...currentNotifications])
    setNotViewNotificationsCount(notViewNotificationsCount + 1)
  }

  useEffect(() => {
    if (socket)
      socket.on(`NOTIFICATION/CREATE/FOR-YOU/PERSON/${user?.id}`, (params: SocketParams) =>
        setNewNotificationOnSocket(params, notifications)
      )

    return () => {
      if (socket) socket.removeListener(`NOTIFICATION/CREATE/FOR-YOU/PERSON/${user?.id}`)
    }
  }, [user, socket, notifications])

  useEffect(() => {
    if (user?.id) loadNotifications()
  }, [filter])

  return (
    <NotificationsManagerContext.Provider
      value={{
        totalNotifications,
        loadingMoreNotifications,
        loadingNotifications,
        notifications,
        notViewNotificationsCount,
        filter,
        pagination,
        loadNotifications,
        loadMoreNotifications,
        setNotifications,
        setNotViewNotificationsCount,
        setFilter,
        setPagination,
      }}
    >
      {children}
    </NotificationsManagerContext.Provider>
  )
}

export const useNotificationsManager = () => useContext(NotificationsManagerContext)

export default NotificationsManagerProvider
